import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';

const CartContext = createContext();

import { signalerAjoutPanier } from '../lib/mesure';
import { pixelAjoutPanier } from '../lib/pixels';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = 'afrione_panier_v1';
// Au-delà, le panier est considéré comme abandonné : prix et disponibilités
// ont probablement changé, mieux vaut repartir de zéro.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Identifie une ligne du panier.
 *
 * Un même produit peut y figurer deux fois en deux poids différents — 400 g
 * et 900 g de lait Nido sont deux lignes distinctes. L'identifiant du produit
 * seul ne suffit donc plus à désigner une ligne.
 */
export const cleLigne = (produitId, declinaisonId) => `${produitId}::${declinaisonId || ''}`;

/** Prix d'une ligne : celui du poids retenu, à défaut celui de la fiche. */
export const prixLigne = (item) => Number(item.declinaison?.price ?? item.product.price) || 0;

/**
 * Retrouve le poids retenu dans la fiche rechargée.
 *
 * Le commerçant a pu en corriger le prix ou en retirer un : on relit toujours
 * depuis la fiche à jour plutôt que de faire confiance au panier enregistré.
 * Un poids disparu retombe sur le moins cher, comme le fait le serveur.
 */
function retrouverDeclinaison(produit, declinaisonId) {
  const liste = produit?.declinaisons || [];
  if (liste.length === 0) return null;
  return liste.find((d) => d.id === declinaisonId) || liste[0];
}

/**
 * Ne conserve que l'identifiant et la quantité, jamais le produit complet.
 * Un prix recopié dans le navigateur se périmerait à la première modification
 * en boutique : le client verrait l'ancien tarif alors que la commande serait
 * calculée sur le nouveau, côté serveur.
 */
function readStoredRefs() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const data = JSON.parse(raw);
    if (!Array.isArray(data?.items)) return [];
    if (!data.savedAt || Date.now() - data.savedAt > MAX_AGE_MS) return [];
    return data.items
      .filter((item) => item?.id && Number(item.quantity) > 0)
      .map((item) => ({
        id: String(item.id),
        quantity: Number(item.quantity),
        declinaisonId: item.d ? String(item.d) : '',
      }))
      .slice(0, 50);
  } catch {
    // Navigation privée, stockage désactivé… : le panier reste simplement volatil
    return [];
  }
}

function writeStoredRefs(items) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      savedAt: Date.now(),
      items: items.map((item) => ({
        id: item.product.id,
        quantity: item.quantity,
        d: item.declinaison?.id || '',
      })),
    }));
  } catch {
    // Écriture impossible : on n'interrompt pas l'achat pour autant
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  // Tant que le panier enregistré n'est pas relu, on n'écrase pas le stockage
  const restored = useRef(false);

  // Au chargement : on relit les références puis on récupère les produits à
  // jour. Un article supprimé du catalogue ou en rupture est écarté.
  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      const refs = readStoredRefs();
      if (refs.length === 0) {
        restored.current = true;
        return;
      }
      try {
        const results = await Promise.all(refs.map(async (ref) => {
          try {
            const { data } = await axios.get(`${API}/products/${ref.id}`);
            if (!data) return null;
            const declinaison = retrouverDeclinaison(data, ref.declinaisonId);
            // La rupture se juge sur le poids retenu, pas sur le total de la
            // fiche : le 400 g peut manquer alors que le 1 kg reste en rayon.
            const stock = declinaison ? (declinaison.stock ?? 0) : (data.stock ?? 0);
            if (stock <= 0) return null;
            return {
              product: data,
              declinaison,
              quantity: ref.quantity,
              cle: cleLigne(data.id, declinaison?.id),
            };
          } catch {
            return null;   // produit disparu : on l'ignore
          }
        }));
        if (!cancelled) {
          setItems(results.filter(Boolean));
        }
      } finally {
        if (!cancelled) restored.current = true;
      }
    };

    restore();
    return () => { cancelled = true; };
  }, []);

  // Enregistrement à chaque changement, une fois la restauration terminée
  useEffect(() => {
    if (!restored.current) return;
    writeStoredRefs(items);
  }, [items]);

  // `declinaison` : le poids retenu, quand la fiche en propose plusieurs.
  // Sans précision, on retient le moins cher — celui dont le prix s'affiche
  // dans la grille, donc celui que le client croit ajouter.
  const addItem = useCallback((product, quantity = 1, declinaison = null) => {
    const retenue = declinaison || retrouverDeclinaison(product, '');
    const prix = Number(retenue?.price ?? product.price) || 0;
    // Mesuré ici et non sur chaque bouton : la grille, la fiche produit et
    // le rayon du quotidien passent tous par cette fonction.
    signalerAjoutPanier(product.id);
    pixelAjoutPanier({ id: product.id, nom: product.name_fr, prix, quantite: quantity });
    const cle = cleLigne(product.id, retenue?.id);
    setItems(prev => {
      const existingIndex = prev.findIndex(item => item.cle === cle);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity
        };
        return updated;
      }
      return [...prev, { product, declinaison: retenue, quantity, cle }];
    });
  }, []);

  const removeItem = useCallback((cle) => {
    setItems(prev => prev.filter(item => item.cle !== cle));
  }, []);

  const updateQuantity = useCallback((cle, quantity) => {
    if (quantity <= 0) {
      removeItem(cle);
      return;
    }
    setItems(prev => prev.map(item =>
      item.cle === cle ? { ...item, quantity } : item
    ));
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const itemCount = useMemo(() =>
    items.reduce((sum, item) => sum + item.quantity, 0)
  , [items]);

  const subtotal = useMemo(() =>
    items.reduce((sum, item) => sum + (prixLigne(item) * item.quantity), 0)
  , [items]);

  return (
    <CartContext.Provider value={{
      items,
      isOpen,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      openCart,
      closeCart,
      itemCount,
      subtotal
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
